export type BedroomInput = string | number | null | undefined

interface ParsedBedrooms {
	count: number
	isPlus: boolean
}

function parseBedrooms(value: BedroomInput): ParsedBedrooms | null {
	if (value === null || value === undefined) return null

	if (typeof value === 'number') {
		if (!Number.isFinite(value)) return null
		const n = Math.trunc(value)
		if (n < 0) return null
		return { count: n, isPlus: false }
	}

	const trimmed = value.trim()
	if (trimmed === '') return null

	const lower = trimmed.toLowerCase()
	if (lower === 'studio') return { count: 0, isPlus: false }

	const plusMatch = lower.match(/(\d+)\s*\+/)
	if (plusMatch) {
		const n = Number.parseInt(plusMatch[1], 10)
		return { count: n, isPlus: true }
	}

	const numMatch = lower.match(/\d+/)
	if (numMatch) {
		const n = Number.parseInt(numMatch[0], 10)
		return { count: n, isPlus: false }
	}

	return null
}

/**
 * Single source of truth for rendering a property's bedroom count.
 * Studio = 0. Counts render exactly ("7 Bedrooms") — Properties v2 collects
 * real numbers via the custom-count dialog, so there is no "4+" cap. Only an
 * explicit "+" in the stored string (legacy "4+-bedroom" form values / step
 * data) keeps the plus suffix. See PROPERTIES-V2-PLAN.md §9 D9.
 */
export function formatBedrooms(value: BedroomInput): string | null {
	const parsed = parseBedrooms(value)
	if (parsed === null) return null
	if (parsed.count === 0) return 'Studio'
	const plus = parsed.isPlus ? '+' : ''
	return `${parsed.count}${plus} Bedroom${parsed.count === 1 && !parsed.isPlus ? '' : 's'}`
}

/** Compact form for cards / inline summaries: "Studio", "1 Bed", "4+ Beds". */
export function formatBedroomsShort(value: BedroomInput): string | null {
	const parsed = parseBedrooms(value)
	if (parsed === null) return null
	if (parsed.count === 0) return 'Studio'
	const plus = parsed.isPlus ? '+' : ''
	return `${parsed.count}${plus} Bed${parsed.count === 1 && !parsed.isPlus ? '' : 's'}`
}

/**
 * Like formatBedroomsShort but always singular ("Studio", "1 Bed", "3 Bed",
 * "4+ Bed") — the form the owner prefers on the application-detail strip and
 * the reports/PDFs ("2 Bed", not "2 Beds"; mirrors the property detail page).
 */
export function formatBedroomsShortSingular(
	value: BedroomInput,
): string | null {
	const parsed = parseBedrooms(value)
	if (parsed === null) return null
	if (parsed.count === 0) return 'Studio'
	return `${parsed.count}${parsed.isPlus ? '+' : ''} Bed`
}

/**
 * Bare count for a stat tile that already captions "Bed(s)" beneath the value:
 * "Studio" (0), "3", "4+". So a studio reads "Studio" instead of "0". Returns
 * null when there is no value.
 */
export function formatBedroomsCount(value: BedroomInput): string | null {
	const parsed = parseBedrooms(value)
	if (parsed === null) return null
	if (parsed.count === 0) return 'Studio'
	return `${parsed.count}${parsed.isPlus ? '+' : ''}`
}
